/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/member-ordering */
import {getStsToken} from '@/api/upload';
import {calculateFileMD5} from '@/utils/md5';

interface StsResponse {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  endpoint?: string;
  bucketName: string;
  expiresAt?: string; // ISO 字符串，如 "2026-04-09T08:26:32Z"
}

interface UploadRecord {
  uploadId: string;
  partList: Array<{ partNumber: number, eTag: string }>;
}

const KB = 1024;
const MB = 1024 * KB;
const GB = 1024 * MB;
const TB = 1024 * GB;
const BOS_MIN_PART_SIZE = 100 * KB;
const BOS_MAX_PART_SIZE = 5 * GB;
const BOS_MAX_PART_COUNT = 10000;
const BOS_MAX_OBJECT_SIZE = Math.floor(48.8 * TB);
const AUTH_ERROR_CODES = new Set([
    'SignatureDoesNotMatch',
    'RequestTimeTooSkewed',
    'InvalidAccessKeyId',
    'ExpiredToken',
    'SecurityTokenExpired',
    'InvalidToken'
]);

/**
 * BOS 上传封装（支持断点续传 + token 缓存复用）
 */
export class BosUploader {
    private static globalClient: any = null;
    private static readonly uploadRecordCache = new Map<string, UploadRecord>();
    private static globalToken: {
    expireAt: number;
    bucket: string;
    credentials: any;
    sessionToken: string;
    endpoint: string;
  } | null = null;

    private bucket = 'fm-online';
    private readonly job_id: string;
    private ready: Promise<void>;
    private aborted = false;

    constructor(job_id: string) {
        this.job_id = job_id;
        this.ready = this.init();
    }

    /** 终止当前上传实例的所有后续操作，并清理上传记录缓存 */
    abort() {
        this.aborted = true;
        BosUploader.uploadRecordCache.clear();
        BosUploader.globalClient = null;
        BosUploader.globalToken = null;
    }


    /** 普通直传 */
    async upload(key: string, data: string | File | Blob, onProgress?: (percent: number) => void): Promise<string> {
        let authRetryCount = 0;

        while (authRetryCount < 2) {
            await this.ready;
            if (this.aborted) {
                throw new Error('upload aborted');
            }
            const client = BosUploader.globalClient;

            try {
                await client.putObjectFromBlob(this.bucket, key, data);
                onProgress?.(100);
                return this.getUrl(key);
            } catch (err) {
                if (this.isAuthError(err) && authRetryCount < 1) {
                    authRetryCount++;
                    await this.refreshClient();
                    continue;
                }

                if (!(err instanceof Error && err.message === 'upload aborted')) {
                    BosUploader.globalClient = null;
                }
                console.error('上传失败:', err);
                throw err;
            }
        }

        throw new Error('Upload failed after auth retry');
    }

    /** 分块上传（断点续传 + 秒传） */
    async multipartUpload(key: string, file: File, onProgress?: (percent: number) => void): Promise<string> {
        let authRetryCount = 0;

        while (authRetryCount < 2) {
            await this.ready;
            if (this.aborted) {
                throw new Error('upload aborted');
            }

            // 空文件和极小文件不走分块上传，避免无意义分片和空 parts
            if (file.size === 0 || file.size < BOS_MIN_PART_SIZE) {
                return this.upload(key, file, onProgress);
            }

            const {partSize, threads} = BosUploader.getUploadStrategy(file.size);
            const fileHash = await calculateFileMD5(file);

            // 秒传检测
            try {
                await BosUploader.globalClient.headObject(this.bucket, key);
                return this.getUrl(key);
            } catch (err) {
                if (this.isAuthError(err) && authRetryCount < 1) {
                    authRetryCount++;
                    await this.refreshClient();
                    continue;
                }
            }

            try {
                return await this.doMultipartUpload(key, file, fileHash, partSize, threads, onProgress);
            } catch (err) {
                if (this.isAuthError(err) && authRetryCount < 1) {
                    authRetryCount++;
                    this.clearUploadRecord(fileHash);
                    await this.refreshClient();
                    continue;
                }
                throw err;
            }
        }

        throw new Error('Multipart upload failed after auth retry');
    }

    private async doMultipartUpload(
        key: string, file: File, fileHash: string,
        partSize: number, threads: number, onProgress?: (percent: number) => void
    ): Promise<string> {
        const totalParts = Math.ceil(file.size / partSize);
        const maxAttempts = 2;
        let attempt = 0;

        while (attempt < maxAttempts) {
            if (this.aborted) {
                throw new Error('upload aborted');
            }
            const record = await this.resolveUploadRecord(fileHash, key);

            try {
                const tasks = this.createUploadTasks(
                    file, partSize, record.uploadId, this.bucket, key, record.partList
                );
                await this.runWithLimit(tasks, threads,
                    task => this.uploadChunk(task, fileHash, record, onProgress, totalParts));

                if (record.partList.length === 0 && totalParts > 0) {
                    throw new Error('No uploaded parts found before completeMultipartUpload');
                }

                record.partList.sort((a, b) => a.partNumber - b.partNumber);
                await BosUploader.globalClient.completeMultipartUpload(
                    this.bucket, key, record.uploadId, record.partList);

                this.clearUploadRecord(fileHash);
                return this.getUrl(key);
            } catch (err) {
                if (this.shouldRebuildMultipartUpload(err) && attempt < maxAttempts - 1) {
                    attempt++;
                    this.clearUploadRecord(fileHash);
                    console.warn(`uploadId 失效或分块列表异常，重新发起分块上传（第 ${attempt + 1} 次）`, err);
                    continue;
                }
                if (!(err instanceof Error && err.message === 'upload aborted')) {
                    BosUploader.globalClient = null;
                }
                throw err;
            }
        }

        throw new Error('Multipart upload failed after retries');
    }

    /**
     * 根据文件大小决定切片大小和并发数。
     * 目标：单个切片在 5 分钟内上传完（按最低 1 MB/s 估算上限 300 MB），
     * 同时保证总 parts 数不超过 BOS 上限 10,000。
     */
    private static getUploadStrategy(fileSize: number): {partSize: number, threads: number} {
        if (fileSize > BOS_MAX_OBJECT_SIZE) {
            throw new Error('文件过大，超过 BOS Object 最大限制 48.8TB');
        }

        let partSize = 5 * MB;
        let threads = 2;

        if (fileSize < 500 * MB) {
            partSize = 5 * MB;
            threads = 2;
        } else if (fileSize < GB) {
            partSize = 10 * MB;
            threads = 3;
        } else if (fileSize < 2 * GB) {
            partSize = 20 * MB;
            threads = 4;
        } else if (fileSize < 10 * GB) {
            partSize = 50 * MB;
            threads = 4;
        } else if (fileSize < 20 * GB) {
            partSize = 100 * MB;
            threads = 4;
        } else {
            partSize = 200 * MB;
            threads = 4;
        }

        // 约束分片大小满足 BOS 限制（100KB ~ 5GB）
        partSize = Math.max(BOS_MIN_PART_SIZE, Math.min(partSize, BOS_MAX_PART_SIZE));

        // 约束分片总数不超过 10,000
        const minPartSizeByCount = Math.ceil(fileSize / BOS_MAX_PART_COUNT);
        if (minPartSizeByCount > partSize) {
            partSize = minPartSizeByCount;
        }

        partSize = Math.max(BOS_MIN_PART_SIZE, Math.min(partSize, BOS_MAX_PART_SIZE));

        if (Math.ceil(fileSize / partSize) > BOS_MAX_PART_COUNT) {
            throw new Error('分片数量超过 BOS 最大限制 10000，请检查文件大小或分片策略');
        }

        return {partSize, threads};
    }

    /** 获取签名 URL */
    getUrl(key: string, expireSeconds: number = 2592000): string {
        const client = BosUploader.globalClient;
        return client.generatePresignedUrl(
            this.bucket,
            key,
            Math.floor(Date.now() / 1000),
            expireSeconds
        );
    }

    async delete(objectKey: string) {
        await this.ready;
        const client = BosUploader.globalClient;
        await client.deleteObject(this.bucket, objectKey);
    }

    /** 获取或创建 uploadId，并以服务端分块列表为准 */
    private async resolveUploadRecord(fileHash: string, key: string): Promise<UploadRecord> {
        const cached = this.getUploadRecord(fileHash);
        if (cached) {
            const serverParts = await this.checkUploadId(cached.uploadId, key);
            if (serverParts !== null) {
                cached.partList = serverParts;
                this.saveUploadRecord(fileHash, cached);
                return cached;
            }
            this.clearUploadRecord(fileHash);
        }

        const initRes = await BosUploader.globalClient.initiateMultipartUpload(this.bucket, key);
        const record: UploadRecord = {uploadId: initRes.body.uploadId, partList: []};
        this.saveUploadRecord(fileHash, record);
        return record;
    }

    /** 验证 uploadId 是否仍然有效，有效则返回服务端已上传的分块列表 */
    private async checkUploadId(
        uploadId: string, key: string
    ): Promise<Array<{partNumber: number, eTag: string}> | null> {
        try {
            const client = BosUploader.globalClient;
            const res = await client.listParts(this.bucket, key, uploadId);
            return (res.body.parts || []).map((p: any) => ({
                partNumber: p.partNumber,
                eTag: p.eTag
            }));
        } catch {
            return null;
        }
    }

    private isAuthError(err: any): boolean {
        const code = err?.code || err?.body?.code;
        const statusCode = Number(err?.status_code || err?.statusCode || err?.response?.status || 0);
        const message = String(err?.message || err?.body?.message || '').toLowerCase();

        return statusCode === 403
            || AUTH_ERROR_CODES.has(code)
            || message.includes('signaturedoesnotmatch')
            || message.includes('request signature we calculated does not match')
            || message.includes('security token')
            || message.includes('expired token')
            || message.includes('requesttime');
    }

    private async refreshClient(): Promise<void> {
        BosUploader.globalClient = null;
        BosUploader.globalToken = null;
        this.ready = this.init();
        await this.ready;
    }

    private shouldRebuildMultipartUpload(err: any): boolean {
        const code = err?.code || err?.body?.code;
        const statusCode = Number(err?.status_code || err?.statusCode || err?.response?.status || 0);
        const message = String(err?.message || err?.body?.message || '').toLowerCase();

        return code === 'NoSuchUpload'
            || message.includes('nosuchupload')
            || (statusCode === 404 && message.includes('upload'))
            || message.includes('no uploaded parts found')
            || (message.includes('parts') && message.includes('empty'));
    }

    /** 上传分块 */
    private async uploadChunk(task: any, hash: string,
        record: UploadRecord, onProgress?: (percent: number) => void, totalParts?: number) {
        const client = BosUploader.globalClient;
        const blob = task.file.slice(task.start, task.stop + 1);
        let retries = 3;

        while (retries > 0) {
            if (this.aborted) {
                throw new Error('upload aborted');
            }
            try {
                const res = await client.uploadPartFromBlob(task.bucket, task.key, task.uploadId,
                    task.partNumber, task.partSize, blob);
                const etag = res.http_headers.etag;

                if (!record.partList.find(p => p.partNumber === task.partNumber)) {
                    record.partList.push({partNumber: task.partNumber, eTag: etag});
                    record.partList.sort((a, b) => a.partNumber - b.partNumber);
                    this.saveUploadRecord(hash, record);
                }

                if (onProgress && totalParts) {
                    const percent = Math.round((record.partList.length / totalParts) * 100);
                    onProgress(percent);
                }

                return;
            } catch (err) {
                if (this.shouldRebuildMultipartUpload(err)) {
                    throw err;
                }

                retries--;
                if (retries === 0) {
                    throw err;
                }
                console.log(`分块 ${task.partNumber} 上传失败，重试中... 剩余次数: ${retries}`);
            }
        }
    }

    /** 创建任务列表 */
    private createUploadTasks(file: File, partSize: number,
        uploadId: string, bucket: string, key: string, partList: UploadRecord['partList']) {
        let leftSize = file.size;
        let offset = 0;
        let partNumber = 1;
        const tasks: any[] = [];

        while (leftSize > 0) {
            const size = Math.min(leftSize, partSize);
            const start = offset;
            const stop = Math.min(offset + size - 1, file.size - 1);

            if (!partList.find(p => p.partNumber === partNumber)) {
                tasks.push({file, uploadId, bucket, key, partNumber, partSize: size, start, stop});
            }

            leftSize -= size;
            offset += size;
            partNumber++;
        }

        return tasks;
    }

    /** 并发控制 */
    private async runWithLimit(tasks: any[], limit: number, worker: (task: any) => Promise<void>) {
        const executing = new Set<Promise<void>>();

        for (const task of tasks) {
            if (this.aborted) {
                throw new Error('upload aborted');
            }
            const p = worker(task).finally(() => executing.delete(p));
            executing.add(p);
            if (executing.size >= limit) {
                await Promise.race(executing);
            }
        }
        await Promise.all(executing);
    }


    /** 上传记录缓存（仅内存，不跨页面刷新） */
    private getUploadRecord(hash: string): UploadRecord | null {
        return BosUploader.uploadRecordCache.get(hash) || null;
    }

    private saveUploadRecord(hash: string, record: UploadRecord) {
        BosUploader.uploadRecordCache.set(hash, record);
    }

    private clearUploadRecord(hash: string) {
        BosUploader.uploadRecordCache.delete(hash);
    }


    /** 确保 bucket 存在 */
    private async ensureBucket(): Promise<void> {
        try {
            await BosUploader.globalClient.doesBucketExist(this.bucket);
        } catch (err) {
            console.log('检查 bucket 出错:', err);
        }
    }
    /** 初始化（带 token 复用机制） */
    private async init(): Promise<void> {
    // 如果已经存在有效 token，则直接复用（重建 client 以确保 sessionToken 一致）
        if (BosUploader.globalToken && BosUploader.globalToken.expireAt > Date.now()) {
            this.bucket = BosUploader.globalToken.bucket;
            if (!BosUploader.globalClient) {
                BosUploader.globalClient = new baidubce.sdk.BosClient({
                    credentials: {
                        ...BosUploader.globalToken.credentials,
                        sessionToken: BosUploader.globalToken.sessionToken
                    },
                    endpoint: BosUploader.globalToken.endpoint
                });
            }
            return;
        }

        // 否则重新获取 STS token
        const res = await getStsToken(this.job_id);
        if (res.code !== '0') {
            throw new Error(`获取STS失败: ${res.message}`);
        }

        const data: StsResponse = res.data;

        const expireAt = data.expiresAt && new Date(data.expiresAt).getTime() > Date.now()
            ? new Date(data.expiresAt).getTime()
            : Date.now() + 50 * 60 * 1000;

        const endpoint = data.endpoint || 'https://bj.bcebos.com';

        const bosConfig = {
            credentials: {ak: data.accessKeyId, sk: data.secretAccessKey},
            sessionToken: data.sessionToken,
            endpoint
        };

        // 创建新的 BOS 客户端
        BosUploader.globalClient = new baidubce.sdk.BosClient(bosConfig);

        // 缓存 token 信息（供下次复用）
        BosUploader.globalToken = {
            expireAt,
            bucket: data.bucketName || 'fm-online',
            credentials: bosConfig.credentials,
            sessionToken: data.sessionToken,
            endpoint
        };

        this.bucket = data.bucketName || 'fm-online';

        await this.ensureBucket();
    }
}
